import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Play, Eye, Trash2, X, BarChart3 } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { allocationsApi, AllocationRule, AllocationBasis, AllocationInput, AllocationPreview } from '@/lib/allocations';
import { accountsApi } from '@/lib/accounts';
import { forecastsApi } from '@/lib/forecasts';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { ConfirmModal } from '@/components/ConfirmModal';

const today = new Date().toISOString().slice(0, 10);
const oneYearAgo = `${Number(today.slice(0, 4)) - 1}${today.slice(4)}`;
const BASES: AllocationBasis[] = ['manual', 'headcount', 'by-account', 'equal'];

export function AllocationPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState<{ rule: AllocationRule; data: AllocationPreview } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const rules = useQuery({ queryKey: ['allocations', wsId], queryFn: () => allocationsApi.list(wsId!), enabled: !!wsId, retry: false });
  const { data: accounts = [] } = useQuery({ queryKey: ['accounts', wsId], queryFn: () => accountsApi.list(wsId!), enabled: !!wsId });
  const { data: scenarios = [] } = useQuery({ queryKey: ['scenarios', wsId], queryFn: () => forecastsApi.scenarios(wsId!), enabled: !!wsId });
  const leafAccounts = accounts.filter((a) => a.kind !== 'section' && a.code);
  const scenarioOpts = useMemo(() => Array.from(new Set(['budget', 'actual', ...scenarios.map((s) => s.scenario)])), [scenarios]);

  const create = useMutation({
    mutationFn: (body: AllocationInput) => allocationsApi.create(wsId!, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['allocations', wsId] }); setShowForm(false); },
  });
  const run = useMutation({
    mutationFn: (id: string) => allocationsApi.run(wsId!, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['management-pl', wsId] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => allocationsApi.remove(wsId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allocations', wsId] }),
  });
  const previewMut = useMutation({
    mutationFn: (rule: AllocationRule) => allocationsApi.preview(wsId!, rule.id).then((data) => ({ rule, data })),
    onSuccess: (r) => setPreview(r),
  });

  const planGated = rules.error && isPlanGateError(rules.error) || create.error && isPlanGateError(create.error);

  return (
    <AppShell title={t('allocations.title', { defaultValue: 'Cost allocation' })} subtitle={t('allocations.subtitle', { defaultValue: 'Spread shared cost across departments — feeds the P&L.' })}>
      <main className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('allocations.title', { defaultValue: 'Cost allocation' })} <span className="text-muted-foreground text-lg">費用配賦</span></h1>
            <p className="mt-1 text-ink-600">{t('allocations.subtitle', { defaultValue: 'Spread shared cost across departments — feeds the P&L.' })}</p>
          </div>
          {!planGated && (
            <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
              <Plus className="h-4 w-4" /> {t('allocations.new_rule', { defaultValue: 'New rule' })}
            </button>
          )}
        </div>

        {planGated && wsId && <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('allocations.feature_name', { defaultValue: 'Cost allocation' })} />}

        {showForm && wsId && (
          <RuleForm leafAccounts={leafAccounts} scenarioOpts={scenarioOpts} onCancel={() => setShowForm(false)} onCreate={(b) => create.mutate(b)} pending={create.isPending} error={create.error && !isPlanGateError(create.error) ? ((create.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error') : null} t={t} />
        )}

        {!planGated && (
          <div className="space-y-3">
            {rules.isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              : (rules.data ?? []).length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
                  <p className="text-ink-700 font-medium">{t('allocations.empty', { defaultValue: 'No allocation rules yet.' })}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t('allocations.empty_sub', { defaultValue: 'Create a rule to spread an overhead cost across departments.' })}</p>
                </div>
              ) : (rules.data ?? []).map((r) => (
                <div key={r.id} className="rounded-md border border-border bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink-900">{r.name}</p>
                    <p className="text-xs text-muted-foreground num">
                      {t('allocations.pool', { defaultValue: 'pool' })} <b>{r.pool_account}</b> → {r.target_dimension_key} · {r.basis} · {r.scenario} · {r.period_start}→{r.period_end}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => previewMut.mutate(r)} disabled={previewMut.isPending} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-border hover:bg-foreground/[0.04]"><Eye className="h-3.5 w-3.5" /> {t('allocations.preview', { defaultValue: 'Preview' })}</button>
                    <button onClick={() => run.mutate(r.id)} disabled={run.isPending} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"><Play className="h-3.5 w-3.5" /> {run.isPending ? t('common.loading') : t('allocations.run', { defaultValue: 'Run' })}</button>
                    <button onClick={() => setDeleteId(r.id)} className="text-ink-400 hover:text-warn-600 p-1.5" aria-label={t('common.delete')}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {run.isSuccess && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-[13px] text-emerald-800 flex items-center justify-between gap-2">
            <span>{t('allocations.run_done', { defaultValue: 'Allocation applied. The cost now appears by department in the P&L.' })}</span>
            <Link to={`/app/workspace/${wsId}/pl`} className="font-semibold inline-flex items-center gap-1 hover:underline"><BarChart3 className="h-3.5 w-3.5" /> {t('allocations.view_pl', { defaultValue: 'View P&L →' })}</Link>
          </div>
        )}
      </main>

      {preview && <PreviewPanel rule={preview.rule} data={preview.data} onClose={() => setPreview(null)} onRun={() => { run.mutate(preview.rule.id); setPreview(null); }} t={t} />}
      <ConfirmModal
        open={deleteId !== null}
        title={t('common.delete')}
        body={t('allocations.delete_confirm', { defaultValue: 'Delete this rule and its allocated facts?' })}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={() => { if (deleteId) remove.mutate(deleteId); setDeleteId(null); }}
        onCancel={() => setDeleteId(null)}
      />
    </AppShell>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function RuleForm({ leafAccounts, scenarioOpts, onCancel, onCreate, pending, error, t }: {
  leafAccounts: { code: string | null; label: string }[]; scenarioOpts: string[];
  onCancel: () => void; onCreate: (b: AllocationInput) => void; pending: boolean; error: string | null; t: TFn;
}) {
  const [name, setName] = useState('');
  const [poolAccount, setPoolAccount] = useState(leafAccounts[0]?.code ?? '');
  const [basis, setBasis] = useState<AllocationBasis>('manual');
  const [targetKey, setTargetKey] = useState('dept');
  const [scenario, setScenario] = useState('budget');
  const [periodStart, setPeriodStart] = useState(oneYearAgo);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [accountKey, setAccountKey] = useState(leafAccounts[0]?.code ?? '');
  const [weights, setWeights] = useState<{ member: string; w: string }[]>([{ member: '', w: '1' }, { member: '', w: '1' }]);
  const input = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm';

  const submit = () => {
    const body: AllocationInput = { name: name.trim() || 'Allocation', basis, poolAccount, targetDimensionKey: targetKey.trim() || 'dept', scenario, periodStart, periodEnd };
    if (basis === 'manual') body.config = { weights: Object.fromEntries(weights.filter((w) => w.member.trim() && Number(w.w) > 0).map((w) => [w.member.trim(), Number(w.w)])) };
    if (basis === 'by-account') body.config = { accountKey };
    onCreate(body);
  };
  const canSubmit = poolAccount && (basis !== 'manual' || weights.some((w) => w.member.trim() && Number(w.w) > 0)) && (basis !== 'by-account' || accountKey);

  return (
    <div className="rounded-md border border-border bg-white p-5 space-y-4">
      <h3 className="text-sm font-semibold text-ink-700">{t('allocations.new_rule', { defaultValue: 'New rule' })}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Lbl label={t('allocations.rule_name', { defaultValue: 'Name' })}><input value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="Rent → departments" /></Lbl>
        <Lbl label={t('allocations.pool_account', { defaultValue: 'Pool account (the cost to spread)' })}>
          <select value={poolAccount} onChange={(e) => setPoolAccount(e.target.value)} className={input}>
            {leafAccounts.map((a) => <option key={a.code} value={a.code!}>{a.code} · {a.label}</option>)}
          </select>
        </Lbl>
        <Lbl label={t('allocations.target_dimension', { defaultValue: 'Spread across (dimension key)' })}><input value={targetKey} onChange={(e) => setTargetKey(e.target.value)} className={`num ${input}`} placeholder="dept" /></Lbl>
        <Lbl label={t('allocations.scenario', { defaultValue: 'Scenario' })}>
          <select value={scenario} onChange={(e) => setScenario(e.target.value)} className={input}>{scenarioOpts.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        </Lbl>
        <Lbl label={t('pl.period_start', { defaultValue: 'From' })}><input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={`num ${input}`} /></Lbl>
        <Lbl label={t('pl.period_end', { defaultValue: 'To' })}><input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={`num ${input}`} /></Lbl>
      </div>

      <Lbl label={t('allocations.basis', { defaultValue: 'Driver (how to weight the split)' })}>
        <div className="flex flex-wrap gap-2">
          {BASES.map((b) => (
            <button key={b} type="button" onClick={() => setBasis(b)} className={'px-3 py-1.5 rounded-md border text-xs ' + (basis === b ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-border text-ink-600')}>
              {t(`allocations.basis_${b.replace('-', '_')}`, { defaultValue: b })}
            </button>
          ))}
        </div>
      </Lbl>

      {basis === 'by-account' && (
        <Lbl label={t('allocations.by_account_key', { defaultValue: 'Weight by this account (e.g. revenue per dept)' })}>
          <select value={accountKey} onChange={(e) => setAccountKey(e.target.value)} className={input}>{leafAccounts.map((a) => <option key={a.code} value={a.code!}>{a.code} · {a.label}</option>)}</select>
        </Lbl>
      )}
      {basis === 'manual' && (
        <div>
          <span className="block text-xs font-semibold text-ink-700 mb-1">{t('allocations.manual_weights', { defaultValue: 'Manual weights (member · weight)' })}</span>
          <div className="space-y-2">
            {weights.map((w, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={w.member} onChange={(e) => setWeights((ws) => ws.map((x, j) => j === i ? { ...x, member: e.target.value } : x))} placeholder={t('allocations.member', { defaultValue: 'department / member' })} className={`num ${input}`} />
                <input type="number" value={w.w} onChange={(e) => setWeights((ws) => ws.map((x, j) => j === i ? { ...x, w: e.target.value } : x))} className={`num ${input} max-w-[120px]`} />
                <button onClick={() => setWeights((ws) => ws.filter((_, j) => j !== i))} className="text-ink-400 hover:text-warn-600 p-1"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setWeights((ws) => [...ws, { member: '', w: '1' }])} className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> {t('allocations.add_member', { defaultValue: 'Add member' })}</button>
          </div>
        </div>
      )}
      {(basis === 'headcount' || basis === 'equal') && (
        <p className="text-[11px] text-muted-foreground">{basis === 'headcount'
          ? t('allocations.headcount_hint', { defaultValue: 'Weights come from headcount-plan FTE per department.' })
          : t('allocations.equal_hint', { defaultValue: 'Split equally across the target dimension\'s members.' })}</p>
      )}

      {error && <p className="text-xs text-warn-700">{error}</p>}
      <div className="flex gap-2">
        <button onClick={submit} disabled={pending || !canSubmit} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">{pending ? t('common.loading') : t('allocations.create_rule', { defaultValue: 'Create rule' })}</button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{label}</span>{children}</label>;
}

function PreviewPanel({ rule, data, onClose, onRun, t }: { rule: AllocationRule; data: AllocationPreview; onClose: () => void; onRun: () => void; t: TFn }) {
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-lg h-full bg-background border-l border-border shadow-editorial flex flex-col">
        <header className="h-14 px-5 flex items-center justify-between border-b border-border">
          <div><p className="text-sm font-semibold">{t('allocations.preview_title', { defaultValue: 'Allocation preview' })}</p><p className="text-[11px] text-muted-foreground">{rule.name}</p></div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {data.currencies.length === 0 || data.currencies.every((c) => c.perMember.length === 0) ? (
            <p className="text-sm text-muted-foreground">{t('allocations.preview_empty', { defaultValue: 'Nothing to allocate — check the pool account, period, and weights.' })}</p>
          ) : data.currencies.map((c) => (
            <div key={c.currency}>
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{t('allocations.pool', { defaultValue: 'pool' })} · {c.currency}</p>
              <p className="num text-xl font-semibold text-ink-900 mb-2">{fmt(c.poolTotal)}</p>
              <table className="w-full text-[13px]">
                <thead><tr className="text-muted-foreground text-[10.5px] uppercase tracking-[0.14em] border-b border-border"><th className="py-1.5 text-left font-medium">{t('allocations.member', { defaultValue: 'Member' })}</th><th className="py-1.5 text-right font-medium">{t('allocations.weight', { defaultValue: 'Weight' })}</th><th className="py-1.5 text-right font-medium">{t('allocations.share', { defaultValue: 'Share' })}</th></tr></thead>
                <tbody>
                  {c.perMember.map((m) => (
                    <tr key={m.member} className="border-b border-border/50"><td className="py-1.5 num">{m.member}</td><td className="py-1.5 num text-right text-muted-foreground">{m.weight}</td><td className="py-1.5 num text-right tabular-nums">{fmt(m.share)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">{t('allocations.preview_hint', { defaultValue: 'Running writes these as traceable facts and an offsetting credit on the pool account — the company total is unchanged.' })}</p>
        </div>
        <footer className="p-4 border-t border-border">
          <button onClick={onRun} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><Play className="h-4 w-4" /> {t('allocations.run', { defaultValue: 'Run allocation' })}</button>
        </footer>
      </div>
    </div>
  );
}
